import { AxiosError } from 'axios';
import { FC, PropsWithChildren, useState, useEffect, useCallback, useRef } from 'react';
import { useNotification } from '../../Contexts/NotificationContext';
import { performEnterpriseGPTApiCall, useEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import TermsOfUse from './TermsOfUse';
import { logout } from '../../Security/azureAdAuthentication';
import { appInsights } from '../../AppInsights';
import { useUserContext } from '../../Contexts/UserContext';
import { UserInteractionEventProperties, EventName_UserInteraction, EventProperty_Value_ApplicationType, EventProperty_Value_DeclineToU, EventName_ToUOpen } from '../../store/models/customEvents';
import { hasAcceptedTermsToday, setTermsOfUseAcceptance } from '../../Utils/termsOfUseCookie';

type EnrollementState = 'unknown' | 'enrolled' | 'not-enrolled';

const EnrollmentChecker: FC<PropsWithChildren> = (props: PropsWithChildren) => {
    const { children } = props;
    const [enrollementState, setEnrollementState] = useState<EnrollementState>('unknown');
    const [showTermsDialog, setShowTermsDialog] = useState(false);
    const { dispatchNotification } = useNotification();
    const { userProfile } = useUserContext();
    const enrollmentChecked = useRef(false);

    useEnterpriseGPTApiCall(async (client, abortSignal) => {
        if (enrollmentChecked.current) {
            console.log('[EnrollmentChecker] Already checked, skipping');
            return;
        }
        enrollmentChecked.current = true;

        try {
            await client.isUserEnrolled(abortSignal);
            console.log('[EnrollmentChecker] User is enrolled');
            setEnrollementState('enrolled');
        } catch (e: unknown) {
            console.log('[EnrollmentChecker] User is NOT enrolled');
            setEnrollementState('not-enrolled');
        }
    });

    // Check if user needs to see terms dialog or auto-enroll when enrollment state changes
    useEffect(() => {
        console.log('[EnrollmentChecker] enrollementState changed:', enrollementState);
        
        // Only process when enrollment state is determined
        if (enrollementState === 'unknown') {
            return;
        }
        
        const hasAcceptedToday = hasAcceptedTermsToday();
        console.log('[EnrollmentChecker] hasAcceptedToday:', hasAcceptedToday);
        
        // Show Terms of Use dialog if user hasn't accepted today
        if (!hasAcceptedToday) {
            console.log('[EnrollmentChecker] Showing Terms dialog');
            appInsights.trackEvent({
                name: EventName_ToUOpen,
                properties: {
                    applicationType: EventProperty_Value_ApplicationType,
                    userId: userProfile?.username
                } as UserInteractionEventProperties
            });
            setShowTermsDialog(true);
            // Don't auto-enroll if we need to show the dialog
            return;
        }
        
        // Auto-enroll ONLY if user is not enrolled and has already accepted today
        // and dialog is not currently shown
        if (enrollementState === 'not-enrolled' && hasAcceptedToday && !showTermsDialog) {
            console.log('[EnrollmentChecker] Auto-enrolling user');
            performEnterpriseGPTApiCall(async (client, cancelToken) => {
                try {
                    await client.enroll(cancelToken);
                    setEnrollementState('enrolled');
                } catch {
                    console.log('[EnrollmentChecker] Auto-enrollment failed, showing dialog');
                    setShowTermsDialog(true);
                    dispatchNotification('error.api.post.enrollment', 'error');
                }
            });
        }
    }, [enrollementState, dispatchNotification, userProfile?.username, showTermsDialog]);

    const handleUserConsentToTermsOfUse = useCallback(() => {
        return performEnterpriseGPTApiCall(async (client, cancelToken) => {
            try {
                await client.enroll(cancelToken);
                // Save the acceptance date in cookie
                setTermsOfUseAcceptance();
                setShowTermsDialog(false);
                setEnrollementState('enrolled');
            } catch {
                dispatchNotification('error.api.post.enrollment', 'error');
            }
        });
    }, [dispatchNotification]);

    const handleDecline = useCallback(async () => {
        appInsights.trackEvent({
            name: EventName_UserInteraction,
            properties: {
                applicationType: EventProperty_Value_ApplicationType,
                userId: userProfile?.username,
                interactionType: EventProperty_Value_DeclineToU
            } as UserInteractionEventProperties
        });
        logout();
    }, [userProfile?.username]);

    // If the user is already enrolled AND has accepted today, render the application
    // Otherwise, if we need to show the dialog, show it (and don't allow it to be closed except by user action)
    // Otherwise, still checking enrollment state
    if (enrollementState === 'enrolled' && hasAcceptedTermsToday()) {
        return <>{children}</>;
    } else if (showTermsDialog) {
        return <TermsOfUse open={true} onAccept={handleUserConsentToTermsOfUse} onDecline={handleDecline} />;
    } else {
        // Still checking enrollment state
        return null;
    }
};

export default EnrollmentChecker;
